/*----------------------------------
- HOME: FAQ
----------------------------------*/

/**
 * Home's questions (`section#faq`): the head sticky at the left, then the marketing `FaqList` (one kit `Disclosure` per
 * answer of `HOME_FAQ_ITEMS`, each a `details`, so `#faq details` still matches), in the catalog's order. The answers
 * explain their glossary terms with the marketing `GlossaryTerm` (`FaqRichText`, shared with the pricing page) and keep
 * their published-page links, and opening one fires `faq_expand`.
 *
 * @docs docs/features/website-landing-experience
 * @rule The questions are `HOME_FAQ_ITEMS`, the same list the route publishes as FAQPage structured data
 *       (`toStructuredDataFaqs(HOME_FAQ_ITEMS)`), so the page and its JSON-LD never disagree.
 * @rule An answer's link shows only when its target is a published human page (`isPublishedHumanPageTarget`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { TextLink } from '@uniquedomains/components/kit/text-link/TextLink';
import { FaqList, type FaqEntry } from '@uniquedomains/components/marketing/faq/FaqList';

import useContext from '@/client/context';
import { HOME_FAQ_ITEMS } from '@/client/catalogs/landing/faq';
import FaqRichText from '@/client/pages/landing/components/marketing-page/FaqRichText';
import { HOME_FAQ_HEAD } from '@/client/catalogs/landing/home/homeCopy';
import { useFaqExpand } from '@/client/pages/landing/components/marketing-page/useFaqExpand';
import { isPublishedHumanPageTarget } from '@/common/catalogs/landing/internalLinks';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** Each answer's `details` id: prefixed, so a catalog id ("refund", "limits") never collides with another id of the page. */
const answerId = (index: number, itemId?: string) => `faq-${itemId ?? index}`;

/** Each answer's `details` id and question, for the expand event. */
const EXPAND_ENTRIES = HOME_FAQ_ITEMS.map((item, index) => ({ id: answerId(index, item.id), question: item.question }));

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeFaq = () => {
    const { enforceDomainPublicationLinks, renderedDomainResourcePaths } = useContext();
    const publishedDomainPaths = enforceDomainPublicationLinks ? renderedDomainResourcePaths : undefined;
    const listRef = useFaqExpand<HTMLDivElement>(EXPAND_ENTRIES);
    const entries: FaqEntry[] = HOME_FAQ_ITEMS.map((item, index) => ({
        id: answerId(index, item.id),
        question: item.question,
        // The first answer opens with the page, as the mock's `details open` (web-frame-15). Its toggle fires while the
        // document parses, before the expand listener is attached, so it never counts as a visitor's expand.
        open: index === 0,
        // One answer is one section: a term explained in another answer is explained again here.
        answer: (
            <>
                <p>
                    <FaqRichText text={item.answer} />
                </p>
                {item.link && isPublishedHumanPageTarget(item.link.href, publishedDomainPaths) ? (
                    <TextLink href={item.link.href} label={item.link.label} />
                ) : null}
            </>
        ),
    }));

    return (
        <section class="sec" id="faq">
            <div class="wrap faq">
                <div class="sh rv">
                    <h2>{HOME_FAQ_HEAD.title}</h2>
                    <p>{HOME_FAQ_HEAD.lead}</p>
                </div>
                <div ref={listRef}>
                    <FaqList entries={entries} />
                </div>
            </div>
        </section>
    );
};

export default HomeFaq;
